import { useState } from 'react';
import type { WerfverslagDto } from '@/api/generated';
import { useProject, useSendWerfverslag, useWerfverslagen } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Avatar } from '@/design-system/primitives/Avatar/Avatar';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { formatShortDate, hueForName, toNumber } from '@/lib/format';
import styles from './Werfverslag.module.css';
import {
  BatchTaskDialog,
  WerfverslagCreateDialog,
  WerfverslagEditDialog,
} from './WerfverslagDialogs';

interface SectionProps {
  title: string;
  last?: boolean;
  children: React.ReactNode;
}

function Section({ title, last, children }: SectionProps) {
  return (
    <div className={`${styles.section} ${last ? styles.sectionLast : ''}`}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.sectionBody}>{children}</div>
    </div>
  );
}

// 2026-05-17 — PLANNING + ATTACHMENTS were Phase-1 design fixtures
// (rendered "Storten druklaag verdiep 1 / Levering Pollet 12 ramen" on
// every werfverslag regardless of the actual meeting). The Werfverslag
// entity carries items + attendees, not per-meeting planning or
// attachments. Both removed; the sidebar now only shows the real
// "Reeks werfverslagen" listing and the suggestion to batch-create tasks.

export function Werfverslag() {
  const reports = useWerfverslagen({ pageSize: 20 });
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <AppFrame>
      <QueryBoundary query={reports} loading="Werfverslagen laden…">
        {(page) => {
          const items = page.items;
          if (items.length === 0) {
            return (
              <>
                <div className={`row ${styles.headerRow}`}>
                  <div>
                    <h1 className={styles.title}>Werfverslagen</h1>
                    <p className={styles.subtitle}>
                      Verslagen van werfvergaderingen — wie was er, wat is afgesproken.
                    </p>
                  </div>
                  <div className="ml-auto">
                    <Btn variant="primary" onClick={() => setCreateOpen(true)}>
                      <Icon name="plus" size={14} /> Nieuw werfverslag
                    </Btn>
                  </div>
                </div>
                <WerfverslagCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
                <div role="alert" className="mono c-ink-3">
                  Nog geen werfverslagen opgesteld.
                </div>
              </>
            );
          }
          const current = items[0];
          return (
            <>
              <WerfverslagBody current={current} all={items} onCreate={() => setCreateOpen(true)} />
              <WerfverslagCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
            </>
          );
        }}
      </QueryBoundary>
    </AppFrame>
  );
}

function WerfverslagBody({
  current,
  all,
  onCreate,
}: {
  current: WerfverslagDto;
  all: WerfverslagDto[];
  onCreate: () => void;
}) {
  const [editOpen, setEditOpen] = useState(false);
  // Pull the project address from the API so the "ter plekke" subtitle
  // reflects the user's actual site instead of the seeded "Bertem" string.
  const project = useProject();
  const siteLabel = project.data?.address?.trim() || project.data?.name?.trim() || 'op de werf';
  const [batchOpen, setBatchOpen] = useState(false);
  const sendVerslag = useSendWerfverslag();

  return (
    <>
      <div className={`row gap-6 ${styles.crumb}`}>
        <span>Werfverslagen</span>
        <Icon name="chevR" size={11} />
        <span className={styles.crumbCurrent}>
          Verslag #{toNumber(current.number)} · {formatShortDate(current.date)}
        </span>
        <Btn variant="ghost" className={`ml-auto ${styles.crumbAction}`} onClick={onCreate}>
          <Icon name="plus" size={12} /> Nieuw werfverslag
        </Btn>
        <Btn variant="ghost" className={styles.crumbAction} onClick={() => setEditOpen(true)}>
          <Icon name="edit" size={12} /> Bewerk
        </Btn>
        <Btn
          variant="ghost"
          className={styles.crumbAction}
          onClick={() => sendVerslag.mutate(current.id)}
          disabled={sendVerslag.isPending || current.attendees.length === 0}
        >
          <Icon name="mail" size={12} /> {sendVerslag.isPending ? 'Versturen…' : 'Verstuur'}
        </Btn>
      </div>
      <WerfverslagEditDialog open={editOpen} onOpenChange={setEditOpen} werfverslag={current} />
      <BatchTaskDialog open={batchOpen} onOpenChange={setBatchOpen} werfverslag={current} />

      <div className={styles.body}>
        <Card className={styles.main}>
          <div className={`row ${styles.headerRow}`}>
            <div>
              <div className="eyebrow">Werfverslag · #{toNumber(current.number)} in deze fase</div>
              <h1 className={styles.title}>
                Werfvergadering — verslag #{toNumber(current.number)}
              </h1>
              <p className={styles.subtitle}>
                {formatShortDate(current.date)} · {siteLabel}
              </p>
            </div>
            <div className="ml-auto">
              <Pill kind="warn" dot={false}>
                {current.phaseId}
              </Pill>
            </div>
          </div>

          <hr className={styles.divider} />

          <div className={styles.presentBlock}>
            <div className={`eyebrow ${styles.presentLabel}`}>Aanwezig</div>
            <div className={`row gap-12 ${styles.presentList}`}>
              {current.attendees.map((attendee) => (
                <div key={attendee.id} className="row gap-8">
                  <Avatar name={attendee.name} size={32} hue={hueForName(attendee.name)} />
                  <div>
                    <div className={styles.presentName}>{attendee.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Section title="Besproken items">
            <ul className={styles.list}>
              {current.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          {/* "Planning komende twee weken" Section removed 2026-05-17 —
              the Werfverslag entity has no planning field. */}
        </Card>

        <div className="col gap-16">
          <Card className={styles.sideCard}>
            <h3>Reeks werfverslagen</h3>
            <div className={`col gap-2 ${styles.weekList}`}>
              {all.slice(0, 5).map((w, i) => (
                <div
                  key={w.id}
                  className={`row gap-8 ${styles.weekRow} ${i === 0 ? styles.weekActive : ''}`}
                >
                  <span className="mono">#{toNumber(w.number)}</span>
                  <span>· {formatShortDate(w.date)}</span>
                  <Icon name="chevR" size={11} />
                </div>
              ))}
              {all.length > 5 && (
                <span className={styles.weekMore}>+ {all.length - 5} oudere verslagen</span>
              )}
            </div>
          </Card>

          {/* "Bijlagen" card removed 2026-05-17 — Werfverslag has no
              attachment relation on the API; attachments live as separate
              Documents linked to the same phase. */}

          <Card className={styles.suggestion}>
            <div className="row gap-6">
              <Icon name="star" size={14} color="var(--info)" />
              <h3 className={styles.suggestionTitle}>Slimme suggestie</h3>
            </div>
            <p className={styles.suggestionBody}>
              Wil je de actiepunten als taken in je takenlijst toevoegen?
            </p>
            <Btn
              className={styles.suggestionBtn}
              onClick={() => setBatchOpen(true)}
              disabled={current.items.length === 0}
            >
              <Icon name="plus" size={13} /> Maak taken aan
            </Btn>
          </Card>
        </div>
      </div>
    </>
  );
}
